import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type EarlyRole = 'early_buyer' | 'early_seller' | 'user' | 'admin' | 'moderator' | null;

export function useEarlyRole() {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    queryKey: ['early-role', user?.id ?? 'anon'],
    enabled: !!user && !authLoading,
    queryFn: async (): Promise<EarlyRole> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      if (error) throw error;
      const roles = (data ?? []).map((r: any) => r.role as string);
      const priority = ['admin', 'moderator', 'early_seller', 'early_buyer', 'user'];
      return (priority.find((r) => roles.includes(r)) as EarlyRole) ?? null;
    },
    staleTime: 60_000,
  });

  return {
    role: query.data ?? null,
    isLoading: authLoading || query.isLoading,
    isSeller:
      query.data === 'early_seller' ||
      query.data === 'admin' ||
      query.data === 'moderator',
    isBuyer: query.data === 'early_buyer',
    refetch: query.refetch,
  };
}

/**
 * Helper used by AuthCallback to invoke the claim-early-role edge function.
 */
export async function claimEarlyRole(role?: 'buyer' | 'seller'): Promise<EarlyRole> {
  const { data, error } = await supabase.functions.invoke('claim-early-role', {
    body: { role: role ? `early_${role}` : undefined },
  });
  if (error) throw error;
  return (data?.role ?? null) as EarlyRole;
}
